// src/utils/createOLE2FrameMeshFromXlsx.js
//
// xlsx ArrayBuffer + bbox alir, OLE2FRAME mesh uretir. Mesh'in userData'si
// import edilmis bir OLE2FRAME ile birebir aynidir; export pipeline (passthrough)
// dogal olarak isler ve dxfDrawCore'un asynchronous render kolu xlsx'i okuyup
// canvas'a cizer (Workbook stream → SheetJS → canvas).

import * as THREE from 'three';
import { buildXlsxOleBlob, parseXlsxBorders, parseXlsxDrawings, XLSX_DRAWINGS_EMU_PER_PX } from '../modules/documents';

/**
 * xlsx'in dogal totalW/totalH'ini (sutun genisligi + satir yuksekligi
 * toplamlari) hesaplar — mesh aspect ratio'sunu xlsx'e gore ayarlamak icin.
 */
async function computeXlsxNaturalDims(xlsxBytes) {
  const XLSX = (await import('xlsx')).default || (await import('xlsx'));
  const wb = XLSX.read(xlsxBytes, { type: 'array', cellStyles: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws || !ws['!ref']) return null;
  const range = XLSX.utils.decode_range(ws['!ref']);
  const nCols = range.e.c - range.s.c + 1;
  const nRows = range.e.r - range.s.r + 1;
  const COL_DEF = 70, ROW_DEF = 22;
  let totalW = 0;
  for (let i = 0; i < nCols; i++) {
    const ci = (ws['!cols'] || [])[range.s.c + i];
    totalW += ci?.wpx || (ci?.wch ? Math.round(ci.wch * 7) : COL_DEF);
  }
  let totalH = 0;
  for (let i = 0; i < nRows; i++) {
    const ri = (ws['!rows'] || [])[range.s.r + i];
    if (ri?.hidden || ri?.hpx === 0) continue;
    totalH += ri?.hpx ?? (ri?.hpt ? Math.round(ri.hpt * 4 / 3) : ROW_DEF);
  }
  return { totalW, totalH };
}

/**
 * @param {Object} opts
 * @param {ArrayBuffer|Uint8Array} opts.xlsxBytes
 * @param {{x:number,y:number,z?:number}} opts.upperLeft  - WCS (DXF) slot koordinatı
 * @param {{x:number,y:number,z?:number}} opts.lowerRight
 * @param {string} [opts.layerName='0']
 * @returns {Promise<THREE.Mesh>} OLE2FRAME mesh; sahneye add ile eklenir.
 */
export async function createOLE2FrameMeshFromXlsx({
  xlsxBytes,
  upperLeft,
  lowerRight,
  layerName = '0',
}) {
  const oleBlob = await buildXlsxOleBlob(xlsxBytes);
  const oleHex = bytesToHex(oleBlob);

  const ul = upperLeft;
  const lr = lowerRight;
  const slotW = Math.abs(lr.x - ul.x) || 1;
  const slotH = Math.abs(ul.y - lr.y) || 1;

  // Mesh boyutlarını xlsx'in doğal aspect'ine göre ayarla — slot içine sığacak
  // şekilde "fit-by-width" veya "fit-by-height" hangisi daha kısıtlayıcıysa.
  // Aksi halde mesh aspect ile canvas aspect uyuşmaz, içerik gerilir
  // (satırlar olduğundan uzun, sağ taraftaki sütunlar dar görünür).
  let meshW = slotW, meshH = slotH;
  const naturalDims = await computeXlsxNaturalDims(xlsxBytes);
  if (naturalDims && naturalDims.totalW > 0 && naturalDims.totalH > 0) {
    const naturalAspect = naturalDims.totalW / naturalDims.totalH;
    const slotAspect    = slotW / slotH;
    if (naturalAspect > slotAspect) {
      // xlsx slot'tan daha geniş aspect — width'e fit, height'i kısalt
      meshW = slotW;
      meshH = slotW / naturalAspect;
    } else {
      // slot'tan daha dar aspect — height'e fit, width'i daralt
      meshH = slotH;
      meshW = slotH * naturalAspect;
    }
  }

  // Mesh'i slot'un üst-orta kısmına yerleştir (header'ın hemen altı).
  const cx = (ul.x + lr.x) / 2;
  const cy = ul.y - meshH / 2;
  // Geriye dönük uyumluluk: w/h değişkenlerini mesh boyutuna göre ayarla
  const w = meshW, h = meshH;

  // Placeholder canvas — dxfDrawCore xlsx'i async olarak Workbook stream'inden
  // okuyup uzerine cizecek. Ayni renderer kolunu kullanmak icin mesh'in
  // userData'sini import edilmis bir OLE2FRAME'in formatinda hazirliyoruz,
  // sonra dxfDrawCore'a benzer bir setTimeout block ile texture'i guncelliyoruz.
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#d0d0d0';
  ctx.fillRect(0, 0, 512, 512);
  ctx.fillStyle = '#666666';
  ctx.font = 'bold 32px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Yükleme Cetveli', 256, 240);
  ctx.font = '20px sans-serif';
  ctx.fillText('(OLE — yükleniyor)', 256, 290);

  const texture = new THREE.CanvasTexture(canvas);
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(w, h),
    new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide })
  );
  mesh.position.set(cx, cy, ul.z || 0);

  // dxfDrawCore.js OLE2FRAME case'i ile uyumlu userData
  mesh.userData.source       = 'dxf';
  mesh.userData.type         = 'ole2frame';
  mesh.userData.binaryHex    = oleHex;
  mesh.userData.oleWidth     = w;
  mesh.userData.oleHeight    = h;
  mesh.userData.layerName    = layerName;
  mesh.userData.itemName     = 'Microsoft Excel Çalışma Sayfası';
  mesh.userData.linkName     = 'OLE';
  mesh.userData.oleVersion   = 2;
  mesh.userData.oleType      = 2;
  mesh.userData.plotQuality  = 0;
  // App-üretimi OLE işareti: export, iç position header'ı dokümanın gerçek
  // konumuna göre yeniden yazsın (template'ten gelen eski koordinatlar değil) ve
  // header değiştiği için 254-char yeniden chunk'lasın. İmport edilmiş OLE'lerde
  // bu bayrak YOK → orijinal blob/chunk yapısı aynen korunur.
  mesh.userData.oleAppGenerated = true;

  // Asenkron olarak xlsx'i Workbook stream'inden cek ve canvas'a render et.
  // dxfDrawCore'daki render mantigini cagiramayiz (case bloku icinde lokal),
  // ama xlsx'i dogrudan SheetJS ile okuyup ayni cell render kodunu
  // generateYuklemeCetveliXlsx pipeline'inda zaten uretiyoruz. En kolayi:
  // xlsx bytes'i alip ayri bir render fonksiyonu cagirmak. Simdilik
  // placeholder gosteriyoruz; dxfDrawCore re-import ya da dxfDraw eklemesi
  // sirasinda gercek render zaten devreye girer.
  // ⚠️ AWAIT: render + binaryHex(presentation) rebuild bitmeden mesh'i DÖNDÜRME.
  // Aksi halde layout'un Promise.all'ı buna beklemiyor ve hızlı export'ta
  // presentation'sız (boş) OLE çıkabiliyordu (büyük doküman geç bitiyor).
  await renderXlsxOnCanvas(xlsxBytes, mesh, canvas, texture);

  return mesh;
}

/** Verilen metni canvas context'inde maxW genişliğine sığacak satırlara böl. */
function wrapTextToWidth(ctx, text, maxW) {
  if (!text) return [''];
  const words = String(text).split(/\s+/);
  const lines = [];
  let cur = '';
  for (const w of words) {
    const trial = cur ? cur + ' ' + w : w;
    if (ctx.measureText(trial).width <= maxW || !cur) {
      cur = trial;
    } else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [text];
}

function bytesToHex(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const arr = new Array(u8.length);
  for (let i = 0; i < u8.length; i++) {
    arr[i] = u8[i].toString(16).padStart(2, '0').toUpperCase();
  }
  return arr.join('');
}

/** xlsx → canvas render (dxfDrawCore'daki ayni mantik, basitlestirilmis). */
async function renderXlsxOnCanvas(xlsxBytes, mesh, canvas, texture) {
  try {
    const XLSX = (await import('xlsx')).default || (await import('xlsx'));
    const wb = XLSX.read(xlsxBytes, { type: 'array', cellStyles: true });
    const wsName = wb.SheetNames[0];
    const ws = wb.Sheets[wsName];
    if (!ws || !ws['!ref']) return;

    // SheetJS Community border'ları okumuyor — manuel parse et
    const cellBorders = await parseXlsxBorders(xlsxBytes);
    // Excel "Ekle > Şekiller" line/connector shape'leri
    const drawings = await parseXlsxDrawings(xlsxBytes);

    const range  = XLSX.utils.decode_range(ws['!ref']);
    const nRows  = range.e.r - range.s.r + 1;
    const nCols  = range.e.c - range.s.c + 1;
    const COL_DEF = 70, ROW_DEF = 22;
    const colWidths  = Array.from({ length: nCols }, (_, i) => {
      const ci = (ws['!cols'] || [])[range.s.c + i];
      return ci?.wpx || (ci?.wch ? Math.round(ci.wch * 7) : COL_DEF);
    });
    const rowHeights = Array.from({ length: nRows }, (_, i) => {
      const ri = (ws['!rows'] || [])[range.s.r + i];
      // hidden veya hpx===0 ise satırı sıfır yükseklikte render et — generator
      // kullanılmayan satırları bu yolla saklıyor.
      if (ri?.hidden || ri?.hpx === 0) return 0;
      return ri?.hpx ?? (ri?.hpt ? Math.round(ri.hpt * 4 / 3) : ROW_DEF);
    });
    const totalW = colWidths.reduce((a, b) => a + b, 0);
    const totalH = rowHeights.reduce((a, b) => a + b, 0);
    // Yüksek çözünürlük için MAX_DIM 4096; scale üst sınırı 4 (text crispliği).
    const MAX_DIM = 4096;
    const scale = Math.min(4, MAX_DIM / Math.max(totalW, totalH, 1));
    const colX = [0]; for (let i = 0; i < nCols; i++) colX.push(colX[i] + colWidths[i] * scale);
    const rowY = [0]; for (let i = 0; i < nRows; i++) rowY.push(rowY[i] + rowHeights[i] * scale);
    const canvasW = Math.min(Math.ceil(totalW * scale), MAX_DIM);
    const canvasH = Math.min(Math.ceil(totalH * scale), MAX_DIM);

    const exC = document.createElement('canvas');
    exC.width = canvasW;
    exC.height = canvasH;
    const ctx = exC.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvasW, canvasH);

    const hidden = new Set(); const merges = {};
    for (const m of (ws['!merges'] || [])) {
      merges[`${m.s.r},${m.s.c}`] = m;
      for (let mr = m.s.r; mr <= m.e.r; mr++)
        for (let mc = m.s.c; mc <= m.e.c; mc++)
          if (mr !== m.s.r || mc !== m.s.c) hidden.add(`${mr},${mc}`);
    }
    const baseFs = Math.max(8, Math.round(11 * scale));

    for (let r = range.s.r; r <= range.e.r; r++) {
      const ri = r - range.s.r;
      for (let c = range.s.c; c <= range.e.c; c++) {
        const ci = c - range.s.c;
        if (hidden.has(`${r},${c}`)) continue;
        const m = merges[`${r},${c}`];
        const x = colX[ci], y = rowY[ri];
        // Defansif: merge end'i range dışına taşarsa range sınırına clamp et.
        // Aksi halde colX/rowY[idx] undefined döner, w/h NaN olur, hücre çizilmez.
        let w, h;
        if (m) {
          const endCi = Math.min(m.e.c - range.s.c + 1, nCols);
          const endRi = Math.min(m.e.r - range.s.r + 1, nRows);
          w = colX[endCi] - x;
          h = rowY[endRi] - y;
        } else {
          w = colWidths[ci] * scale;
          h = rowHeights[ri] * scale;
        }
        const cell = ws[XLSX.utils.encode_cell({ r, c })]; const s = cell?.s || {};
        const bg = s.fgColor?.rgb || s.bgColor?.rgb;
        const hasFill = !!(bg && bg !== 'FFFFFF' && bg !== 'ffffff');
        ctx.fillStyle = hasFill ? '#' + bg : '#ffffff';
        ctx.fillRect(x, y, w, h);

        // Default border ARTIK ÇİZİLMİYOR — sadece template'in styles.xml'inde
        // explicit tanımlanmış kenarlıklar render edilir. cellBorders map'i
        // (parseXlsxBorders'tan) her hücre için hangi kenarın hangi style'a
        // sahip olduğunu söyler.
        const addr = XLSX.utils.encode_cell({ r, c });
        const explicitBorder = cellBorders.get(addr);
        if (explicitBorder) {
          // Production: siyah, Excel border style'larına göre kalınlık.
          const drawBorder = (side, x1, y1, x2, y2) => {
            const b = explicitBorder[side];
            if (!b?.style || b.style === 'none') return;
            ctx.strokeStyle = b.color?.rgb ? '#' + b.color.rgb : '#000000';
            // Excel border style → relatif kalınlık (scale ile orantılı):
            //   thin (default 1pt), medium (1.5pt), thick (2pt+)
            //   double/dashed/dotted/hair/dashDot tek satıra eşitlenir.
            const widthMul =
              b.style === 'thick'  ? 2.5 :
              b.style === 'medium' ? 1.6 :
              b.style === 'double' ? 1.8 :
              b.style === 'hair'   ? 0.6 :
                                     1;
            ctx.lineWidth = Math.max(1, scale * widthMul);
            ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
          };
          drawBorder('top',    x,     y,     x + w, y);
          drawBorder('right',  x + w, y,     x + w, y + h);
          drawBorder('bottom', x,     y + h, x + w, y + h);
          drawBorder('left',   x,     y,     x,     y + h);
        }

        if (!cell) continue;
        const txt = cell.w != null ? String(cell.w) : (cell.v != null ? String(cell.v) : '');
        if (!txt) continue;
        // Kontrast için: font boyutunu biraz büyüt (1.5x), tüm yazılar minimum
        // 600 weight (Excel "bold" stili 800'e yükseltilir). Fontlar düz Arial
        // yerine Arial Black/Helvetica fallback'ine düşmesin diye 'sans-serif'
        // bırakıyoruz; tarayıcı 600 yoksa en yakına (genelde 700 = bold) çevirir.
        const fsPx = s.font?.sz ? Math.round(s.font.sz * scale * 1.5) : Math.round(baseFs * 1.15);
        const weight = s.font?.bold ? '800' : '600';
        ctx.font = `${s.font?.italic ? 'italic ' : ''}${weight} ${fsPx}px Arial, sans-serif`;
        const fc = s.font?.color?.rgb;
        ctx.fillStyle = (fc && fc.toUpperCase() !== 'FFFFFF') ? '#' + fc : '#000000';
        // Tüm hücrelerde text yatay+dikey merkezde — Excel'in sol/sağ
        // alignment'ı yerine üniform görünüm istiyoruz.
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'middle';
        ctx.save(); ctx.beginPath(); ctx.rect(x, y, w, h); ctx.clip();

        // wrapText aktifse veya tek satır hücreye sığmıyorsa kelimeden böl.
        const wantWrap = !!s.alignment?.wrapText;
        const padX = Math.max(2, 2 * scale);
        const maxW = w - 2 * padX;
        const measure = ctx.measureText(txt).width;
        if (wantWrap || measure > maxW) {
          const lines = wrapTextToWidth(ctx, txt, maxW);
          const lineH = fsPx * 1.15;
          const totalLineH = lineH * lines.length;
          const startY = y + h / 2 - totalLineH / 2 + lineH / 2;
          lines.forEach((ln, li) => {
            ctx.fillText(ln, x + w / 2, startY + li * lineH);
          });
        } else {
          ctx.fillText(txt, x + w / 2, y + h / 2);
        }
        ctx.restore();
      }
    }

    // ── Excel "Ekle > Şekiller" line/connector shape'lerini çiz ──
    // Strateji: shape frame'inde transform uygulayıp endpoints'i normalize
    // et (0..1 aralığında), sonra ANCHOR bbox'a (from/to cell pozisyonları)
    // map et. Bu sayede absolute EMU pozisyonu Excel'in default cell sizing'ine
    // bağlı olmaz; anchor cell'lerimizdeki gerçek pixel pozisyonu kullanılır.
    if (drawings.length > 0) {
      const emuToPx = scale / XLSX_DRAWINGS_EMU_PER_PX;
      const anchorToPixel = (anchor) => {
        const ci = anchor.col - range.s.c;
        const ri = anchor.row - range.s.r;
        const baseX = (ci >= 0 && ci < colX.length) ? colX[ci] : 0;
        const baseY = (ri >= 0 && ri < rowY.length) ? rowY[ri] : 0;
        return {
          x: baseX + anchor.colOff * emuToPx,
          y: baseY + anchor.rowOff * emuToPx,
        };
      };

      for (const ln of drawings) {
        if (!ln.ext) continue;
        const cx = ln.ext.cx || 1, cy = ln.ext.cy || 1;

        // 1) Shape frame'inde line endpoints
        let p1 = { x: 0,  y: 0 };
        let p2 = { x: cx, y: cy };

        // 2) flipH/flipV (shape merkezi etrafında mirror)
        if (ln.flipH) { p1.x = cx - p1.x; p2.x = cx - p2.x; }
        if (ln.flipV) { p1.y = cy - p1.y; p2.y = cy - p2.y; }

        // 3) rot — bbox merkezi etrafında
        if (ln.rotDeg) {
          const rad = ln.rotDeg * Math.PI / 180;
          const cos = Math.cos(rad), sin = Math.sin(rad);
          const hX = cx / 2, hY = cy / 2;
          const rotPt = (p) => ({
            x: hX + (p.x - hX) * cos - (p.y - hY) * sin,
            y: hY + (p.x - hX) * sin + (p.y - hY) * cos,
          });
          p1 = rotPt(p1);
          p2 = rotPt(p2);
        }

        // 4) Transformed endpoints'in bbox'i — bunu (0..1, 0..1)'e normalize et
        const minX = Math.min(p1.x, p2.x), maxX = Math.max(p1.x, p2.x);
        const minY = Math.min(p1.y, p2.y), maxY = Math.max(p1.y, p2.y);
        const W = Math.max(1e-10, maxX - minX);
        const H = Math.max(1e-10, maxY - minY);
        const t1 = { x: (p1.x - minX) / W, y: (p1.y - minY) / H };
        const t2 = { x: (p2.x - minX) / W, y: (p2.y - minY) / H };

        // 5) Anchor bbox (canvas pixel) — from = TL, to = BR (varsayılan)
        const a = anchorToPixel(ln.from);
        const b = anchorToPixel(ln.to);
        const aX = Math.min(a.x, b.x), aY = Math.min(a.y, b.y);
        const aW = Math.abs(b.x - a.x), aH = Math.abs(b.y - a.y);

        // 6) Normalized → anchor bbox map
        const c1 = { x: aX + t1.x * aW, y: aY + t1.y * aH };
        const c2 = { x: aX + t2.x * aW, y: aY + t2.y * aH };

        ctx.strokeStyle = ln.colorRgb ? '#' + ln.colorRgb : '#000000';
        const lineWpx = ln.widthEmu > 0 ? ln.widthEmu * emuToPx : Math.max(1, scale * 1);
        ctx.lineWidth = Math.max(1, lineWpx);
        ctx.beginPath();
        ctx.moveTo(c1.x, c1.y);
        ctx.lineTo(c2.x, c2.y);
        ctx.stroke();
      }
    }

    const tex = new THREE.CanvasTexture(exC);
    // Crispness: mipmap kullanma, linear filter, anisotropy yüksek tut.
    tex.minFilter   = THREE.LinearFilter;
    tex.magFilter   = THREE.LinearFilter;
    tex.generateMipmaps = false;
    tex.anisotropy  = 16;
    tex.needsUpdate = true;
    mesh.material.map = tex;
    mesh.material.needsUpdate = true;
    texture.dispose();

    // ── OLE presentation güncelle (AutoCAD'in göstereceği DIB) ──
    // Render edilmiş tabloyu makul boyuta küçült, OlePres000'i bu DIB ile yeniden
    // kurup mesh.binaryHex'i güncelle. Böylece DXF export (passthrough) GÖRÜNÜR bir
    // OLE2FRAME yazar. (Eski blob'da OlePres silinmişti → AutoCAD'de boş görünüyordu.)
    // Doküman mesh'i kaydedilmediği için (userData.source='dxf') bu binaryHex büyümesi
    // .jbz'yi etkilemez; yalnızca export sırasında DXF'e gider.
    try {
      const PRES_MAX = 1200;
      const ps = Math.min(1, PRES_MAX / Math.max(exC.width, exC.height));
      const pw = Math.max(1, Math.round(exC.width * ps));
      const ph = Math.max(1, Math.round(exC.height * ps));
      const pc = document.createElement('canvas');
      pc.width = pw; pc.height = ph;
      const pctx = pc.getContext('2d');
      pctx.fillStyle = '#ffffff';
      pctx.fillRect(0, 0, pw, ph);
      pctx.drawImage(exC, 0, 0, pw, ph);
      const pid = pctx.getImageData(0, 0, pw, ph);
      const oleBlob = await buildXlsxOleBlob(xlsxBytes, { data: pid.data, width: pw, height: ph });
      mesh.userData.binaryHex = bytesToHex(oleBlob);
      delete mesh.userData.hexChunks; // 254-char yeniden chunk'lansın
    } catch (presErr) {
      console.warn('[createOLE2FrameMeshFromXlsx] OLE presentation güncellenemedi:', presErr.message);
    }
  } catch (err) {
    console.warn('[createOLE2FrameMeshFromXlsx] xlsx render hatasi:', err);
  }
}

export default createOLE2FrameMeshFromXlsx;
